import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Clock, Info } from 'lucide-react';

export default function ContratosPage() {
  const { apiFetch, user } = useAuth();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentContract, setCurrentContract] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    centro_coste: '',
    expected_hours_daily: '',
    expected_hours_weekly: '',
    expected_hours_monthly: '',
    expected_days_monthly: '',
    night_shift_start: '22:00:00',
    night_shift_end: '06:00:00',
    alert_enabled: true,
    alert_frequency: 'daily',
    alert_emails: '',
    description: ''
  });

  useEffect(() => {
    loadContracts();
  }, []);

  const loadContracts = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/contracts');
      if (res.ok) {
        const data = await res.json();
        setContracts(data.data || []);
      }
    } catch (error) {
      toast.error('Error al cargar contratos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditMode(false);
    setCurrentContract(null);
    setFormData({
      name: '',
      centro_coste: '',
      expected_hours_daily: '',
      expected_hours_weekly: '',
      expected_hours_monthly: '',
      expected_days_monthly: '',
      night_shift_start: '22:00:00',
      night_shift_end: '06:00:00',
      alert_enabled: true,
      alert_frequency: 'daily',
      alert_emails: '',
      description: ''
    });
    setShowDialog(true);
  };

  const handleEdit = (contract) => {
    setEditMode(true);
    setCurrentContract(contract);
    setFormData({
      name: contract.name || '',
      centro_coste: contract.centro_coste || '',
      expected_hours_daily: contract.expected_hours_daily || '',
      expected_hours_weekly: contract.expected_hours_weekly || '',
      expected_hours_monthly: contract.expected_hours_monthly || '',
      expected_days_monthly: contract.expected_days_monthly || '',
      night_shift_start: contract.night_shift_start || '22:00:00',
      night_shift_end: contract.night_shift_end || '06:00:00',
      alert_enabled: contract.alert_enabled !== false,
      alert_frequency: contract.alert_frequency || 'daily',
      alert_emails: (contract.alert_emails || []).join(', '),
      description: contract.description || ''
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.centro_coste.trim()) {
      toast.error('Nombre y Centro de Coste son obligatorios');
      return;
    }

    const payload = {
      ...formData,
      expected_hours_daily: parseFloat(formData.expected_hours_daily) || null,
      expected_hours_weekly: parseFloat(formData.expected_hours_weekly) || null,
      expected_hours_monthly: parseFloat(formData.expected_hours_monthly) || null,
      expected_days_monthly: parseInt(formData.expected_days_monthly) || null,
      alert_emails: formData.alert_emails.split(',').map(e => e.trim()).filter(Boolean)
    };

    try {
      const url = editMode ? `/api/contracts/${currentContract._id}` : '/api/contracts';
      const method = editMode ? 'PUT' : 'POST';
      
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const error = await res.json();
        if (res.status === 403) {
          toast.error(
            (error.detail || 'Sin permisos') +
            '. Si sólo quieres configurar los horarios de turnos (mañana/tarde/noche), usa la sección "Turnos por centro".',
            { duration: 8000 }
          );
          return;
        }
        throw new Error(error.detail || 'Error al guardar contrato');
      }

      toast.success(editMode ? 'Contrato actualizado' : 'Contrato creado');
      setShowDialog(false);
      loadContracts();
    } catch (error) {
      toast.error(error.message || 'Error al guardar');
    }
  };

  const handleDelete = async (contractId) => {
    if (!window.confirm('¿Estás seguro de eliminar este contrato?')) return;

    try {
      const res = await apiFetch(`/api/contracts/${contractId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Contrato eliminado');
        loadContracts();
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEvaluate = async (contract) => {
    const today = new Date().toISOString().slice(0, 10);
    const firstDayMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

    toast.info('Evaluando cumplimiento con IA...');

    try {
      const res = await apiFetch(
        `/api/contracts/${contract._id}/evaluate?period_start=${firstDayMonth}&period_end=${today}&send_alerts=true`,
        { method: 'POST' }
      );

      if (!res.ok) throw new Error('Error al evaluar');

      const result = await res.json();
      toast.success(`Evaluación completada. ${result.deviations_count} desviaciones detectadas.`);
      
      // Show AI summary in a modal or alert
      if (result.ai_summary) {
        setTimeout(() => {
          alert(`Resumen IA:\n\n${result.ai_summary}`);
        }, 500);
      }
    } catch (error) {
      toast.error('Error al evaluar contrato');
    }
  };

  return (
    <div style={{ padding: '32px', overflowY: 'auto', height: '100vh' }}>
      {/* Aviso informativo — cabecera aclarando la confusión frecuente */}
      <div
        data-testid="contratos-info-banner"
        style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px',
          background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.3)',
          borderRadius: '10px', padding: '12px 14px', marginBottom: '18px',
        }}
      >
        <Info size={16} style={{ color: '#60a5fa', flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.55 }}>
          <strong style={{ color: '#f1f5f9' }}>¿Buscas configurar horarios/turnos por centro?</strong>{' '}
          Este módulo es para <strong>contratos de servicio</strong> (SLAs, horas contratadas, alertas de incumplimiento).
          Para grabar los horarios de mañana/tarde/noche que se aplican a los fichajes, ve a{' '}
          <button
            type="button"
            data-testid="contratos-go-turnos"
            onClick={() => navigate('/turnos')}
            style={{
              background: 'transparent', border: 'none', padding: 0,
              color: '#60a5fa', fontWeight: 600, cursor: 'pointer',
              textDecoration: 'underline', fontSize: 12,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <Clock size={12} /> Turnos por centro
          </button>.
        </div>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontFamily: 'Space Grotesk', fontSize: '24px', fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
            Contratos
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>
            Gestiona contratos y alertas de cumplimiento
          </p>
        </div>
        <Button
          onClick={handleCreate}
          data-testid="create-contract-button"
          style={{ background: '#2563eb', color: 'white', gap: '8px', display: 'flex', alignItems: 'center' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Nuevo Contrato
        </Button>
      </div>

      {/* Contracts Grid */}
      {loading ? (
        <p style={{ color: '#64748b' }}>Cargando...</p>
      ) : contracts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#1e293b', borderRadius: '12px', border: '1px solid #334155' }}>
          <p style={{ color: '#64748b', fontSize: '14px' }}>No hay contratos configurados</p>
          <Button onClick={handleCreate} style={{ marginTop: '16px', background: '#2563eb', color: 'white' }}>
            Crear primer contrato
          </Button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
          {contracts.map(contract => (
            <motion.div
              key={contract._id}
              style={{
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '12px',
                padding: '20px',
                cursor: 'pointer'
              }}
              whileHover={{ borderColor: '#3b82f6' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div>
                  <h3 style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: 600, margin: '0 0 4px 0' }}>
                    {contract.name}
                  </h3>
                  <p style={{ color: '#64748b', fontSize: '12px', fontFamily: 'IBM Plex Mono', margin: 0 }}>
                    {contract.centro_coste}
                  </p>
                </div>
                {contract.alert_enabled && (
                  <span style={{ 
                    background: '#22c55e20', 
                    color: '#22c55e', 
                    padding: '4px 8px', 
                    borderRadius: '6px', 
                    fontSize: '10px',
                    fontWeight: 600
                  }}>
                    Alertas ON
                  </span>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                {contract.expected_hours_monthly && (
                  <div style={{ background: '#0f172a', padding: '10px', borderRadius: '6px' }}>
                    <div style={{ color: '#64748b', fontSize: '10px', marginBottom: '2px' }}>Horas/mes</div>
                    <div style={{ color: '#60a5fa', fontSize: '16px', fontWeight: 600 }}>
                      {contract.expected_hours_monthly}h
                    </div>
                  </div>
                )}
                {contract.expected_days_monthly && (
                  <div style={{ background: '#0f172a', padding: '10px', borderRadius: '6px' }}>
                    <div style={{ color: '#64748b', fontSize: '10px', marginBottom: '2px' }}>Días/mes</div>
                    <div style={{ color: '#34d399', fontSize: '16px', fontWeight: 600 }}>
                      {contract.expected_days_monthly}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  size="sm"
                  onClick={() => handleEvaluate(contract)}
                  style={{ flex: 1, background: '#2563eb', color: 'white', fontSize: '11px' }}
                >
                  Evaluar Ahora
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(contract)}
                  style={{ background: '#0f172a', border: '1px solid #334155', color: '#94a3b8', fontSize: '11px' }}
                >
                  Editar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(contract._id)}
                  style={{ color: '#ef4444', fontSize: '11px' }}
                >
                  🗑️
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[600px]" style={{ background: '#1e293b', border: '1px solid #334155', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: 600 }}>
              {editMode ? 'Editar Contrato' : 'Nuevo Contrato'}
            </DialogTitle>
          </DialogHeader>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '10px 0' }}>
            <div>
              <Label style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                Nombre del contrato *
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Contrato Limpieza Madrid Norte"
                style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}
              />
            </div>

            <div>
              <Label style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                Centro de Coste *
              </Label>
              <Input
                value={formData.centro_coste}
                onChange={(e) => setFormData({ ...formData, centro_coste: e.target.value })}
                placeholder="Ej: MADRID_001"
                style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <Label style={{ color: '#cbd5e1', fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  Horas/día
                </Label>
                <Input
                  type="number"
                  value={formData.expected_hours_daily}
                  onChange={(e) => setFormData({ ...formData, expected_hours_daily: e.target.value })}
                  placeholder="8"
                  style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', height: '36px' }}
                />
              </div>
              <div>
                <Label style={{ color: '#cbd5e1', fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  Horas/semana
                </Label>
                <Input
                  type="number"
                  value={formData.expected_hours_weekly}
                  onChange={(e) => setFormData({ ...formData, expected_hours_weekly: e.target.value })}
                  placeholder="40"
                  style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', height: '36px' }}
                />
              </div>
              <div>
                <Label style={{ color: '#cbd5e1', fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  Horas/mes
                </Label>
                <Input
                  type="number"
                  value={formData.expected_hours_monthly}
                  onChange={(e) => setFormData({ ...formData, expected_hours_monthly: e.target.value })}
                  placeholder="160"
                  style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', height: '36px' }}
                />
              </div>
            </div>

            <div>
              <Label style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                Días trabajados esperados por mes
              </Label>
              <Input
                type="number"
                value={formData.expected_days_monthly}
                onChange={(e) => setFormData({ ...formData, expected_days_monthly: e.target.value })}
                placeholder="22"
                style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <Label style={{ color: '#cbd5e1', fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  Turno noche inicia (HH:MM:SS)
                </Label>
                <Input
                  value={formData.night_shift_start}
                  onChange={(e) => setFormData({ ...formData, night_shift_start: e.target.value })}
                  placeholder="22:00:00"
                  style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', height: '36px' }}
                />
              </div>
              <div>
                <Label style={{ color: '#cbd5e1', fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  Turno noche termina (HH:MM:SS)
                </Label>
                <Input
                  value={formData.night_shift_end}
                  onChange={(e) => setFormData({ ...formData, night_shift_end: e.target.value })}
                  placeholder="06:00:00"
                  style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', height: '36px' }}
                />
              </div>
            </div>

            <div>
              <Label style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                Emails para alertas (separados por comas)
              </Label>
              <Input
                value={formData.alert_emails}
                onChange={(e) => setFormData({ ...formData, alert_emails: e.target.value })}
                placeholder="manager1@valorizasm.com, manager2@valorizasm.com"
                style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}
              />
            </div>

            <div>
              <Label style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                Descripción (opcional)
              </Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                placeholder="Notas adicionales sobre este contrato..."
                style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={formData.alert_enabled}
                onChange={(e) => setFormData({ ...formData, alert_enabled: e.target.checked })}
                style={{ width: '16px', height: '16px' }}
              />
              <Label style={{ color: '#cbd5e1', fontSize: '13px', margin: 0 }}>
                Activar alertas automáticas por email
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowDialog(false)}
              style={{ color: '#64748b' }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              style={{ background: '#2563eb', color: 'white' }}
            >
              {editMode ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
